import { useCallback, useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { SupplierOrg, SupplierPortalRole, SupplierWorkFlag, VendorCapability } from '../types';
import { supplierWorkFlagsFromOrgCapabilities } from '../utils/supplierWorkFlags';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { normalizeSupplierOrg } from '../utils/vendorHelpers';

export type VendorOrgBasicsDraft = {
  name: string;
  notes: string;
  serviceRegion: string;
};

export type VendorOrgCapabilitiesDraft = {
  capabilities: VendorCapability[];
  cncDefaultFlatFee: string;
  cncDefaultRatePerBdft: string;
  leadTimeWeeks: string;
};

export function supplierInviteStatusLabel(status: string | undefined): string {
  if (status === 'accepted') return 'Account ready';
  if (status === 'pending') return 'Pending send';
  if (status === 'revoked') return 'Revoked';
  return status || '—';
}

export function useVendorOrgEditor(org: SupplierOrg | null) {
  const { user } = useAuth();
  const { updateSupplierOrg, createSupplierInvite } = useCRM();

  const [basicsDraft, setBasicsDraft] = useState<VendorOrgBasicsDraft>({
    name: '',
    notes: '',
    serviceRegion: '',
  });
  const [capabilitiesDraft, setCapabilitiesDraft] = useState<VendorOrgCapabilitiesDraft>({
    capabilities: ['timber'],
    cncDefaultFlatFee: '',
    cncDefaultRatePerBdft: '',
    leadTimeWeeks: '',
  });
  const [notificationEmail, setNotificationEmail] = useState('');
  const [notificationFirstName, setNotificationFirstName] = useState('');
  const [savingBasics, setSavingBasics] = useState(false);
  const [savingCapabilities, setSavingCapabilities] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePortalRole, setInvitePortalRole] = useState<SupplierPortalRole>('owner');
  const [inviteWorkFlags, setInviteWorkFlags] = useState<SupplierWorkFlag[]>([]);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!org) {
      setBasicsDraft({ name: '', notes: '', serviceRegion: '' });
      setCapabilitiesDraft({
        capabilities: ['timber'],
        cncDefaultFlatFee: '',
        cncDefaultRatePerBdft: '',
        leadTimeWeeks: '',
      });
      setNotificationEmail('');
      setNotificationFirstName('');
      setInviteWorkFlags([]);
      setInvitePortalRole('owner');
      return;
    }
    const norm = normalizeSupplierOrg(org);
    setBasicsDraft({
      name: norm.name || '',
      notes: norm.notes || '',
      serviceRegion: norm.serviceRegion || '',
    });
    setCapabilitiesDraft({
      capabilities: norm.capabilities?.length ? [...norm.capabilities] : ['timber'],
      cncDefaultFlatFee: norm.cncDefaultFlatFee != null ? String(norm.cncDefaultFlatFee) : '',
      cncDefaultRatePerBdft: norm.cncDefaultRatePerBdft != null ? String(norm.cncDefaultRatePerBdft) : '',
      leadTimeWeeks: norm.leadTimeWeeks != null ? String(norm.leadTimeWeeks) : '',
    });
    setNotificationEmail(norm.defaultContactEmail?.trim() || '');
    setNotificationFirstName(norm.defaultContactFirstName?.trim() || '');
    setInviteWorkFlags(supplierWorkFlagsFromOrgCapabilities(norm.capabilities));
    setInvitePortalRole('owner');
    setInviteMessage(null);
  }, [org?.id, org?.name, org?.notes, org?.serviceRegion, org?.capabilities, org?.defaultContactEmail, org?.defaultContactFirstName, org?.cncDefaultFlatFee, org?.cncDefaultRatePerBdft, org?.leadTimeWeeks]);

  const saveBasics = useCallback(async () => {
    if (!org?.id || !basicsDraft.name.trim()) return;
    setSavingBasics(true);
    try {
      await updateSupplierOrg(org.id, {
        name: basicsDraft.name.trim(),
        notes: basicsDraft.notes.trim() || undefined,
        serviceRegion: basicsDraft.serviceRegion.trim() || undefined,
      });
    } finally {
      setSavingBasics(false);
    }
  }, [org?.id, basicsDraft, updateSupplierOrg]);

  const saveCapabilities = useCallback(async () => {
    if (!org?.id || capabilitiesDraft.capabilities.length === 0) return;
    setSavingCapabilities(true);
    try {
      const parseNum = (s: string) => {
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : undefined;
      };
      await updateSupplierOrg(org.id, {
        capabilities: capabilitiesDraft.capabilities,
        cncDefaultFlatFee: parseNum(capabilitiesDraft.cncDefaultFlatFee),
        cncDefaultRatePerBdft: parseNum(capabilitiesDraft.cncDefaultRatePerBdft),
        leadTimeWeeks: parseNum(capabilitiesDraft.leadTimeWeeks),
      });
    } finally {
      setSavingCapabilities(false);
    }
  }, [org?.id, capabilitiesDraft, updateSupplierOrg]);

  const saveNotificationEmail = useCallback(async () => {
    if (!org?.id || !notificationFirstName.trim()) return;
    setSavingEmail(true);
    try {
      await updateSupplierOrg(org.id, {
        defaultContactFirstName: notificationFirstName.trim(),
        defaultContactEmail: notificationEmail.trim() || undefined,
      });
    } finally {
      setSavingEmail(false);
    }
  }, [org?.id, notificationEmail, notificationFirstName, updateSupplierOrg]);

  const sendInvite = useCallback(async () => {
    if (!org?.id || !inviteName.trim() || !inviteEmail.trim()) return;
    const flags = inviteWorkFlags.length
      ? inviteWorkFlags
      : supplierWorkFlagsFromOrgCapabilities(org.capabilities);
    if (flags.length === 0) return;
    setInviteSending(true);
    setInviteMessage(null);
    try {
      const inviteId = await createSupplierInvite({
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim(),
        supplierOrgId: org.id,
        supplierDesignations: flags,
        supplierPortalRole: invitePortalRole,
        invitedBy: user?.id || '',
        invitedByName: user?.name || '',
        status: 'pending',
      });
      const functions = getFunctions(undefined, 'us-central1');
      const call = httpsCallable<
        { email: string; name: string; inviteId: string },
        { success: boolean; method?: string; message?: string }
      >(functions, 'sendSupplierInviteCallable');
      const { data } = await call({
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim(),
        inviteId,
      });
      if (data.success) {
        setInviteMessage(
          data.method === 'simulated'
            ? data.message || `[Testing] Invite simulated — no email sent.`
            : data.method === 'email'
              ? `Invite email sent to ${inviteEmail.trim()}`
              : data.message || 'Invite processed.',
        );
        setInviteName('');
        setInviteEmail('');
        setInvitePortalRole('owner');
        setInviteWorkFlags(supplierWorkFlagsFromOrgCapabilities(org.capabilities));
      } else {
        setInviteMessage('Invite failed');
      }
    } catch {
      setInviteMessage('Network error sending invite.');
    } finally {
      setInviteSending(false);
    }
  }, [org?.id, org?.capabilities, inviteName, inviteEmail, invitePortalRole, inviteWorkFlags, user?.id, user?.name, createSupplierInvite]);

  return {
    basicsDraft,
    setBasicsDraft,
    capabilitiesDraft,
    setCapabilitiesDraft,
    notificationEmail,
    setNotificationEmail,
    notificationFirstName,
    setNotificationFirstName,
    savingBasics,
    savingCapabilities,
    savingEmail,
    saveBasics,
    saveCapabilities,
    saveNotificationEmail,
    inviteName,
    setInviteName,
    inviteEmail,
    setInviteEmail,
    invitePortalRole,
    setInvitePortalRole,
    inviteWorkFlags,
    setInviteWorkFlags,
    inviteSending,
    inviteMessage,
    sendInvite,
  };
}
